import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { siteConfig } from '@/config/site';
import {
    BarChart3,
    Bot,
    Puzzle,
    Monitor,
    Layers,
    FlaskConical,
    ArrowRight,
    CheckCircle2,
    Download,
} from 'lucide-react';

const features = [
    {
        icon: BarChart3,
        title: 'Advanced Charting',
        description:
            'Professional-grade charting with 70+ indicators, detachable chart windows, and multi-timeframe analysis for precise market insights.',
    },
    {
        icon: Bot,
        title: `Algorithmic Trading (${siteConfig.platform.automation.botTermShortPlural})`,
        description:
            `Build, backtest, and deploy automated strategies in ${siteConfig.platform.automation.name}. Develop ${siteConfig.platform.automation.botTermPlural} with the built-in MetaEditor and Strategy Tester.`,
    },
    {
        icon: Puzzle,
        title: 'Custom Indicators',
        description:
            `Create your own custom indicators or install from the ${siteConfig.platform.name} community marketplace. Full flexibility for your analysis workflow.`,
    },
    {
        icon: Monitor,
        title: 'Multi-Monitor Support',
        description:
            'Detach charts, watchlists, and panels across multiple monitors. Design the perfect trading workspace for your setup.',
    },
    {
        icon: Layers,
        title: 'Level II Pricing',
        description:
            'Access full market depth with Level II pricing, giving you visibility into the order book and available liquidity at every price level.',
    },
    {
        icon: FlaskConical,
        title: 'Backtesting',
        description:
            'Test your strategies against historical data with advanced backtesting tools. Optimize parameters and validate performance before going live.',
    },
];

const benefits = [
    'Full-powered desktop trading application',
    'Detachable windows for multi-monitor setups',
    `${siteConfig.platform.automation.name} for algorithmic trading`,
    `Access to community indicators and ${siteConfig.platform.automation.botTermShortPlural}`,
    'Advanced backtesting and optimization',
    'Available for Windows and macOS',
];

export default function Desktop() {
    return (
        <AppLayout title={`${siteConfig.platform.shortName} Desktop - ${siteConfig.brand.name}`}>
            {/* Hero */}
            <Section className="bg-background pt-32 lg:pt-40 pb-20">
                <div className="text-center max-w-4xl mx-auto">
                    <FadeIn>
                        <Badge className="mb-6">Desktop Platform</Badge>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
                            {siteConfig.platform.name} <span className="text-gradient">Desktop</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.2}>
                        <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto mb-10">
                            Professional Trading Environment
                        </p>
                    </FadeIn>
                    <FadeIn delay={0.3}>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg">
                                    <Download className="mr-2 h-4 w-4" />
                                    Get Started
                                </Button>
                            </a>
                            <a href={siteConfig.platform.downloadUrl} target="_blank" rel="noopener noreferrer">
                                <Button size="lg" variant="outline">
                                    Download {siteConfig.platform.name}
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </a>
                        </div>
                    </FadeIn>
                </div>
            </Section>

            {/* Features */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Desktop"
                        highlight="Features"
                        description={`Unlock the full power of ${siteConfig.platform.name} with our feature-rich desktop application.`}
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature) => {
                            const Icon = feature.icon;
                            return (
                                <motion.div key={feature.title} variants={staggerItem}>
                                    <Card className="h-full rounded-xl border border-border bg-card/80 backdrop-blur-sm transition-colors hover:border-primary/20">
                                        <CardContent className="p-6 lg:p-8">
                                            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                                <Icon className="h-5 w-5 text-primary" />
                                            </div>
                                            <h3 className="mb-2 text-lg font-semibold text-foreground">
                                                {feature.title}
                                            </h3>
                                            <p className="text-sm leading-relaxed text-muted-foreground">
                                                {feature.description}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </StaggerChildren>
            </Section>

            {/* Benefits */}
            <Section className="bg-background">
                <FadeIn>
                    <SectionHeader
                        title="Why Choose"
                        highlight={`${siteConfig.platform.shortName} Desktop`}
                        description="The most powerful way to trade -- designed for serious traders who demand the best."
                    />
                </FadeIn>
                <FadeIn delay={0.1}>
                    <div className="max-w-2xl mx-auto">
                        <Card className="rounded-xl border border-border bg-card/80 backdrop-blur-sm">
                            <CardContent className="p-8">
                                <StaggerChildren>
                                    <ul className="space-y-4">
                                        {benefits.map((benefit) => (
                                            <motion.li
                                                key={benefit}
                                                variants={staggerItem}
                                                className="flex items-center gap-3 text-muted-foreground"
                                            >
                                                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-primary/10 border border-primary/10 shrink-0">
                                                    <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                                                </div>
                                                <span>{benefit}</span>
                                            </motion.li>
                                        ))}
                                    </ul>
                                </StaggerChildren>
                            </CardContent>
                        </Card>
                    </div>
                </FadeIn>
            </Section>

            {/* CTA */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            Download <span className="text-gradient">{siteConfig.platform.shortName} Desktop</span>
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8">
                            Get the full desktop experience. Download {siteConfig.platform.name} and start trading with institutional-grade tools.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg">
                                    <Download className="mr-2 h-4 w-4" />
                                    Get Started
                                </Button>
                            </a>
                            <a href={siteConfig.platform.downloadUrl} target="_blank" rel="noopener noreferrer">
                                <Button size="lg" variant="outline">
                                    Download {siteConfig.platform.name}
                                    <ArrowRight className="ml-2 h-4 w-4" />
                                </Button>
                            </a>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
